/**
 * Stable codes of the errors a customer or an admin can see, shared by backend and frontend. The frontend
 * translates them with the i18n key `errors.<CODE>` (params interpolated); the message here stays as the
 * Spanish fallback for clients that do not know the code. Keep the frontend keys in sync when adding one.
 */
export const ERROR_MESSAGES = {
	PRODUCT_NOT_FOUND: 'Producto no encontrado',
	PRODUCT_NO_ACTIVE_PROVIDER: 'El producto no tiene un proveedor activo configurado',
	PRODUCT_WITHOUT_PRICES: 'Este producto no tiene precios configurados',
	PRODUCT_PRICES_MISMATCH: 'Cada cantidad necesita su precio: el número de precios y de cantidades debe coincidir',
	PRODUCT_ONE_ACTIVE_PROVIDER: 'El producto debe tener exactamente un proveedor activo',
	PRODUCT_DUPLICATE_IDS: 'La lista tiene productos repetidos',
	COMMENTS_REQUIRED: 'Este producto necesita los comentarios, uno por línea',
	COMMENTS_COUNT_MISMATCH: 'Hay {{count}} comentarios para una cantidad de {{amount}}: deben coincidir',
	QUANTITY_INVALID: 'La cantidad debe ser un número entero mayor que 0',
	QUANTITY_BELOW_MIN: 'La cantidad mínima para este producto es {{min}}',
	QUANTITY_ABOVE_MAX: 'La cantidad máxima para este producto es {{max}}',
	ORDER_NOT_FOUND: 'Orden no encontrada',
	ORDER_FORBIDDEN: 'No tienes permiso para ver este pedido',
	ORDER_NOT_PENDING_PAYMENT: 'Orden no encontrada o no está pendiente de pago',
	PAYMENT_SESSION_MISMATCH: 'La sesión de pago no corresponde a esta orden',
	PAYMENT_NOT_COMPLETED: 'El pago no está completado en Stripe',
	PAYMENT_BELOW_MINIMUM: 'El importe a pagar con tarjeta debe ser de al menos {{min}} €',
	PAYMENT_PROVIDER_ERROR: 'El proveedor de pagos ha rechazado la operación: {{detail}}',
	PAYMENT_PROVIDER_UNAVAILABLE: 'No se pudo completar la operación con el proveedor de pagos, inténtalo de nuevo',
	PROVIDER_BALANCE_INSUFFICIENT:
		'Saldo insuficiente en el proveedor: el pedido cuesta {{cost}} {{currency}} y hay {{balance}} {{currency}}',
	CRYPTO_WALLET_NOT_CONFIGURED: 'El pago con {{currency}} no está disponible ahora mismo',
	WEBHOOK_INVALID: 'Petición de webhook no válida',
	WEBHOOK_FAILED: 'No se pudo procesar el webhook',
	WEBHOOK_NOT_CONFIGURED: 'El webhook de pagos no está configurado',
	ORDER_DUPLICATE: 'Acabas de crear este mismo pedido: revisa tus pedidos antes de repetirlo',
	SUBSCRIPTION_DUPLICATE: 'Acabas de crear esta misma suscripción: revisa tus suscripciones antes de repetirla',
	DISCOUNT_USED_UP: 'El descuento se ha agotado mientras hacías el pedido: vuelve a intentarlo',
	USER_NOT_FOUND: 'Usuario no encontrado',
	USER_ALREADY_BANNED: 'El usuario ya está bloqueado',
	USER_NOT_BANNED: 'El usuario no está bloqueado',
	USER_WITHOUT_AUTH_ACCOUNT: 'El usuario no tiene cuenta de acceso',
	USER_CREATE_FAILED: 'No se pudo crear el usuario, inténtalo de nuevo',
	EMAIL_IN_USE: 'Ese email ya está en uso',
	EMAIL_UPDATE_FAILED: 'No se pudo cambiar el email, inténtalo de nuevo',
	/** Sent with the English message 'Email already registered' (see AuthService.register) */
	EMAIL_ALREADY_REGISTERED: 'Este email ya está registrado',
	REGISTERED_LOGIN_FAILED: 'Tu cuenta se ha creado, pero no se pudo iniciar sesión automáticamente: inicia sesión',
	ACCOUNT_CREATION_FAILED: 'No se pudo crear la cuenta, inténtalo de nuevo',
	FILE_REQUIRED: 'No se ha enviado ningún archivo',
	FILE_TYPE_INVALID: 'Solo se admiten imágenes JPEG, PNG o WebP',
	FILE_TOO_LARGE: 'El archivo supera el límite de {{max}} MB',
	PROVIDER_IN_USE: 'Este proveedor está asignado a {{count}} producto(s): quítalo de ellos antes de borrarlo',
	PROVIDER_NOT_FOUND: 'Proveedor no encontrado',
	PROVIDER_NAME_TAKEN: 'Ya existe un proveedor con este nombre',
	PLATFORM_NOT_FOUND: 'Plataforma no encontrada',
	PLATFORM_NAME_TAKEN: 'Ya existe otra plataforma con el nombre {{name}}',
	ROLE_NOT_FOUND: 'Rol no encontrado',
	ROLES_LOAD_FAILED: 'No se pudieron cargar los roles, inténtalo de nuevo',
	REQUEST_NOT_FOUND: 'Solicitud no encontrada o ya fue procesada',
	DISCOUNT_NOT_FOUND: 'Descuento no encontrado',
	DISCOUNT_PERCENT_RANGE: 'El porcentaje debe estar entre 0 y 100',
	DISCOUNT_DATES: 'La fecha de fin debe ser posterior a la fecha de inicio',
	DISCOUNT_CODE_REQUIRED: 'Un descuento por código necesita el código (y ningún usuario)',
	DISCOUNT_USER_REQUIRED: 'Un descuento por usuario necesita el usuario (y ningún código)',
	DISCOUNT_CODE_TAKEN: 'Ya hay otro descuento activo con el código {{code}}',
	SUBSCRIPTION_NOT_FOUND: 'Suscripción no encontrada',
	SUBSCRIPTION_FORBIDDEN: 'No tienes permiso para esta suscripción',
	SUBSCRIPTION_FINISHED: 'La suscripción ya está finalizada',
	SUBSCRIPTION_NOT_ACTIVE: 'Solo se pueden pausar suscripciones activas',
	SUBSCRIPTION_NOT_PAUSED: 'Solo se pueden reanudar suscripciones pausadas',
	SUBSCRIPTION_NOT_PENDING_PAYMENT: 'La suscripción no está pendiente de pago',
	REQUEST_ALREADY_PROCESSED: 'La solicitud ya fue procesada',
	CANNOT_DELETE_OWN_ACCOUNT: 'No puedes eliminar tu propia cuenta',
	CANNOT_BAN_OWN_ACCOUNT: 'No puedes bloquear tu propia cuenta',
	CANNOT_DEMOTE_OWN_ACCOUNT: 'No puedes quitar el rol de admin a tu propia cuenta',
	LAST_ADMIN: 'Es el último administrador activo: no se puede eliminar, bloquear ni degradar',
	VALIDATION_FAILED: 'Los datos enviados no son válidos',
	INVALID_STORED_VALUE: 'Hay un dato guardado no válido ({{field}}): avisa al administrador',
	INVALID_ID: 'El identificador {{field}} no es válido: debe ser un número entero positivo',
	TOO_MANY_PENDING_ORDERS:
		'Ya tienes {{max}} pagos pendientes: págalos o espera a que se verifiquen antes de crear otro',
	// sessions and access
	AUTH_INVALID_CREDENTIALS: 'Email o contraseña incorrectos',
	AUTH_FAILED: 'No se pudo iniciar sesión, inténtalo de nuevo',
	AUTH_TOKEN_REQUIRED: 'Inicia sesión para continuar',
	SESSION_EXPIRED: 'Tu sesión ha caducado: vuelve a iniciar sesión',
	AUTH_SERVICE_UNAVAILABLE: 'El servicio de acceso no está disponible ahora mismo: inténtalo en un momento',
	USER_NOT_REGISTERED: 'Tu usuario no está dado de alta en el sistema: contacta con el administrador',
	USER_WITHOUT_ROLE: 'Tu usuario no tiene ningún rol asignado: contacta con el administrador',
	USER_BANNED: 'Tu cuenta está bloqueada: contacta con el administrador',
	INSUFFICIENT_PERMISSIONS: 'No tienes permiso para hacer esto',
	DEV_LOGIN_UNAVAILABLE: 'El acceso de desarrollo no está disponible',
	PASSWORD_RESET_EMAIL_FAILED: 'No se pudo enviar el email para restablecer la contraseña, inténtalo de nuevo',
	PASSWORD_RESET_LINK_INVALID: 'El enlace para restablecer la contraseña no es válido o ha caducado: pide otro',
	PASSWORD_UPDATE_FAILED: 'No se pudo cambiar la contraseña, inténtalo de nuevo',
	// auth/security hardening (MFA enforcement, current-password checks, rate limits...)
	MFA_REQUIRED: 'Esta sesión necesita la verificación en dos pasos: vuelve a iniciar sesión',
	MFA_SERVICE_FAILED: 'No se pudo completar la verificación en dos pasos, inténtalo de nuevo',
	MFA_INVALID_CODE: 'El código de verificación no es correcto',
	CURRENT_PASSWORD_REQUIRED: 'Para cambiar el email de tu cuenta indica tu contraseña actual',
	INVALID_CURRENT_PASSWORD: 'La contraseña actual es incorrecta',
	CURRENT_PASSWORD_CHECK_FAILED: 'No se pudo comprobar la contraseña actual, inténtalo de nuevo',
	TOO_MANY_REQUESTS: 'Demasiados intentos: espera un momento y vuelve a probar',
	NOTIFICATION_NOT_FOUND: 'Notificación no encontrada',
	SYSTEM_ROLE_REQUIRED: 'Falta el rol de sistema {{role}}: revisa la configuración de roles',
} as const;

export type ErrorCode = keyof typeof ERROR_MESSAGES;

/** Params interpolated into an error message (`{{name}}` placeholders) */
export type ErrorParams = Record<string, string | number>;

/** Whether a value is one of the known error codes */
export function isErrorCode(value: unknown): value is ErrorCode {
	return typeof value === 'string' && Object.hasOwn(ERROR_MESSAGES, value);
}

/** The known error code of an API error body (`{ code, message, params }`), if it carries one */
export function errorCodeOf(body: unknown): ErrorCode | undefined {
	const code = (body as { code?: unknown } | null | undefined)?.code;
	return isErrorCode(code) ? code : undefined;
}

/** The Spanish fallback message of a code, with its params interpolated */
export function formatErrorMessage(code: ErrorCode, params?: ErrorParams): string {
	return ERROR_MESSAGES[code].replace(/\{\{(\w+)\}\}/g, (_, key: string) => String(params?.[key] ?? ''));
}
